import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLeagueTipData } from "./hooks/useLeagueTipData.ts";
import { useCupTipData } from "./hooks/useCupTipData.ts";
import { TouchBackend } from "react-dnd-touch-backend";
import { DndProvider } from "react-dnd";
import { supabase } from "./supaBaseClient.ts";
// Lightweight components stay eager so initial render is fast.
import SiteFooter from "./components/SiteFooter.tsx";
import CookieHint from "./components/CookieHint.tsx";
import AppHeader from "./components/AppHeader.tsx";
import LeagueSelector from "./components/LeagueSelector.tsx";
import { type LegalPageKind } from "./components/LegalPage.tsx";

// Heavy views lazy-loaded — they only render once the user navigates
// into the relevant flow, and each pulls in different dependencies
// (DnD, annexC JSON, supabase admin lookups). Code-splitting these
// drops the initial bundle by ~150-200KB pre-gzip.
const LandingPage = lazy(() => import("./components/LandingPage.tsx"));
const TipRoundDashboard = lazy(
  () => import("./components/TipRoundDashboard.tsx"),
);
const TipRoundDetail = lazy(
  () => import("./components/TipRoundDetail.tsx"),
);
const LegalPage = lazy(() => import("./components/LegalPage.tsx"));
const TipComparisonPanel = lazy(
  () => import("./components/comparison/TipComparisonPanel.tsx"),
);
const CupComparisonPanel = lazy(
  () => import("./components/comparison/CupComparisonPanel.tsx"),
);
const RankingsTable = lazy(
  () => import("./components/RankingsTable.tsx"),
);
const TournamentRankingsTable = lazy(
  () => import("./components/TournamentRankingsTable.tsx"),
);
const TipTable = lazy(() => import("./components/TipTable.tsx"));
const MatchTipTable = lazy(() => import("./components/MatchTipTable.tsx"));
const TournamentGroupView = lazy(
  () => import("./components/TournamentGroupView.tsx"),
);
const CLTournamentView = lazy(
  () => import("./components/CLTournamentView.tsx").then((m) => ({ default: m.CLTournamentView })),
);
const InviteFriendsModal = lazy(
  () => import("./components/InviteFriendsModal.tsx"),
);
const ProfileEditModal = lazy(
  () => import("./components/ProfileEditModal.tsx"),
);
const SpecialQuestionsPanel = lazy(
  () => import("./components/SpecialQuestionsPanel.tsx"),
);
const LeagueDramaPanel = lazy(
  () => import("./components/LeagueDramaPanel.tsx"),
);
const AdminRoundsPanel = lazy(
  () => import("./components/AdminRoundsPanel.tsx"),
);
const AdminOddsPanel = lazy(
  () => import("./components/AdminOddsPanel.tsx"),
);

// Generic spinner shown while a lazy chunk loads. Plain CSS class
// already exists in index.css for the main loading state.
const ChunkSpinner: React.FC = () => (
  <div className="loading-container">
    <div className="loading">…</div>
  </div>
);
import { leagueService } from "./services/LeagueService.ts";
import { tipRoundService } from "./services/TipRoundService.ts";
import {
  needsRealDisplayName,
  profileService,
} from "./services/ProfileService.ts";
import type {
  AppState,
  League,
  Standing,
  TipRound,
  TipWithProfile,
  User,
} from "./types/index.ts";

// Resolves the tipp-lock date for a competition.
// Primary source: league.lock_date (set via DB migration 001 / 021). Fallback to the
// previously hardcoded values for environments where the migration has not run
// yet (transitional safety net).
//
// WM 2026 exact lock: 2026-06-11 18:00 UTC = 22:00 VAE (GST) = 20:00 CEST.
// This is checked before the generic lock_date lookup because the DB column
// may still be a DATE type (truncated to 00:00 UTC) until migration 021 runs.
const WM2026_LOCK = new Date("2026-06-11T18:00:00Z"); // 22:00 VAE / 20:00 CEST

// Sentinels for the two cases where no lock_date is available.
const LOCKED = new Date(-8640000000000000); // always in the past → locked
const OPEN = new Date(8640000000000000); // always in the future → open

const getSeasonStartDate = (league: League | null): Date => {
  // WM 2026 uses a precise timestamp — takes precedence over the DATE column
  if (league?.code === "wm2026") return WM2026_LOCK;
  if (league?.lock_date) {
    return new Date(league.lock_date);
  }
  // Nothing loaded yet: stay locked until the real competition arrives, so the
  // table is never briefly draggable during the initial render.
  if (!league) return LOCKED;
  // Competition without a lock_date: treat as open rather than silently
  // locking it. Every live competition carries a lock_date in the DB.
  return OPEN;
};

function App() {
  const { t } = useTranslation();
  // Der Spieler, dessen Spalte im Tipp-Vergleich hervorgehoben ist. Lebt hier,
  // weil Rangliste und Vergleich Geschwister sind: die eine setzt, der andere liest.
  const [highlightedUserId, setHighlightedUserId] = useState<string | null>(null);
  const [state, setState] = useState<AppState>({
    standings: [],
    loading: true,
    user: null,
    userTip: null,
    allTips: [],
    profile: null,
    currentLeague: null,
    availableLeagues: [],
    leagueStandings: {},
    userTips: {},
    allTipsByLeague: {},
    combinedRankings: [],
    isMobile: globalThis.innerWidth < 1024,
    showInviteModal: false,
    showUserMenu: false,
    isBeforeSeasonStart: new Date() < getSeasonStartDate(null),
    currentTipRound: null,
    myTipRounds: [],
  });

  // Spieltipps, Spielplan und gesperrte Sonderfragen der Liga-Runde. Einmal
  // geholt, von Rangliste und Tipp-Vergleich gemeinsam gelesen.
  const leagueTipData = useLeagueTipData(
    state.currentLeague?.type === "LEAGUE" ? state.currentTipRound?.id : undefined,
    state.currentLeague?.type === "LEAGUE" ? state.currentLeague?.id : undefined,
  );

  // Champions League: Tipps, Spielplan, Phasen, Sonderfragen — und die
  // Ligaphasen-Tabelle, die der CUP-Zweig oben bewusst nicht mitlädt. Ohne sie
  // bekam calculateCLScore nie eine echte Reihenfolge und die Rangliste stand
  // für alle auf 0.
  const cupTipData = useCupTipData(
    state.currentLeague?.type === "CUP" ? state.currentLeague?.id : undefined,
    state.currentLeague?.type === "CUP" ? state.currentTipRound?.id : undefined,
    state.currentLeague?.type === "CUP" ? state.currentLeague?.code : undefined,
  );
  const cupLeaguePhaseOrder = useMemo(
    () => cupTipData.data.standings.map((s) => s.TeamName),
    [cupTipData.data.standings],
  );

  // Transient routing state — kept local rather than in AppState.
  const [managingRound, setManagingRound] = useState<TipRound | null>(null);
  const [pendingJoinCode, setPendingJoinCode] = useState<string | null>(null);
  const [legalPage, setLegalPage] = useState<LegalPageKind | null>(null);
  // When true (and user is logged in), show the marketing LandingPage so
  // the user can revisit the rules / scoring / FAQ without logging out.
  // Logo-click in the AppHeader sets this; the LandingPage gets an
  // onBackToApp callback to clear it again.
  const [showLanding, setShowLanding] = useState(false);
  // Profile-name modal: visibility is *derived* from current state, not stored.
  // - Auto-shown whenever the profile lacks a real display name (the "required"
  //   nudge), gated on profileLoaded so we don't flash the modal during the
  //   brief window where state.user is set but state.profile is still loading.
  // - Manually opened via the Hi-pill in the header.
  // Using derived state avoids the bug where an effect would open the modal
  // when profile was null mid-load and never close it once the real name
  // arrived.
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [manualEdit, setManualEdit] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showAdminOddsPanel, setShowAdminOddsPanel] = useState(false);

  // Platform-owner-only admin panel — hardcoded to specific email, NOT group-admin role.
  // state.user.email can be undefined if the Supabase session is restored from a cached
  // token that predates the email field; fall back to decoding the JWT payload directly.
  const isAdmin = (() => {
    if (!state.user) return false;
    if (state.user.email) return state.user.email === "v1cke@online.dev";
    try {
      const raw = localStorage.getItem("sb-fvuwfonfrllbjqvywula-auth-token");
      if (!raw) return false;
      const p = JSON.parse(raw) as Record<string, unknown>;
      const token =
        (p?.access_token as string | undefined) ??
        ((p?.currentSession as Record<string, unknown> | undefined)
          ?.access_token as string | undefined);
      if (!token) return false;
      // JWT uses base64url (no padding, - and _ instead of + and /).
      // atob() needs standard base64 with padding, so we convert first.
      const b64url = token.split(".")[1];
      const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
      const padded = b64.padEnd(b64.length + (4 - b64.length % 4) % 4, "=");
      const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
      return (payload?.email as string | undefined) === "v1cke@online.dev";
    } catch {
      return false;
    }
  })();
  const needsName =
    profileLoaded && !!state.user && needsRealDisplayName(state.profile);
  const showProfileEdit = !!state.user && (needsName || manualEdit);

  // On first mount, look for a share-link query param (?join=CODE).
  // Persist to localStorage so it survives the magic-link redirect cycle
  // (login email → click link → return to app → no more ?join=). After a
  // successful join the Dashboard clears the key.
  useEffect(() => {
    const PENDING_KEY = "rankbet_pending_join";
    const params = new URLSearchParams(globalThis.location.search);
    const fromUrl = params.get("join");

    let join: string | null = fromUrl;
    if (!join) {
      try {
        join = localStorage.getItem(PENDING_KEY);
      } catch {
        // localStorage might be blocked (privacy mode); silently ignore
      }
    }

    if (fromUrl) {
      // First arrival via share link — persist so we survive a future
      // magic-link round-trip, and clean the URL so a refresh doesn't
      // re-trigger.
      try {
        localStorage.setItem(PENDING_KEY, fromUrl.toUpperCase());
      } catch {
        // ignore
      }
      params.delete("join");
      const newQuery = params.toString();
      const newUrl =
        globalThis.location.pathname +
        (newQuery ? `?${newQuery}` : "") +
        globalThis.location.hash;
      globalThis.history.replaceState({}, "", newUrl);
    } else if (join) {
      // We're consuming the relay on a subsequent load, not first arrival.
      // Clear localStorage immediately so a stale wrong code doesn't haunt
      // every future reload. The Dashboard's onJoinCodeConsumed callback
      // additionally clears pendingJoinCode state once the auto-join has run.
      try {
        localStorage.removeItem(PENDING_KEY);
      } catch {
        // ignore
      }
    }

    if (join) {
      setPendingJoinCode(join.toUpperCase());
    }
  }, []);

  // Responsive Breakpoint Handler
  useEffect(() => {
    const handleResize = () => {
      setState((prev: AppState) => ({
        ...prev,
        isMobile: globalThis.innerWidth < 1024,
      }));
    };

    globalThis.addEventListener("resize", handleResize);
    return () => globalThis.removeEventListener("resize", handleResize);
  }, []);

  // Theme the body background per competition type (CSS reads the attribute).
  useEffect(() => {
    const type = state.currentLeague?.type ?? "LEAGUE";
    document.body.setAttribute("data-competition-type", type);
    return () => {
      document.body.removeAttribute("data-competition-type");
    };
  }, [state.currentLeague?.type]);

  // Reset competition state when user logs out. (Active competitions are
  // now loaded by handleSelectTipRound after the user picks a tip round, so
  // we don't auto-load all leagues on login any more.)
  useEffect(() => {
    if (!state.user) {
      setState((prev: AppState) => ({
        ...prev,
        loading: false,
        availableLeagues: [],
        currentLeague: null,
      }));
    }
  }, [state.user]);

  // tipRoundId explizit mitgeben, wenn die Runde gerade erst gesetzt wurde:
  // direkt nach setState hält diese Closure noch das alte state.currentTipRound
  // (beim ersten Öffnen null) — dann käme der Nutzertipp nie aus tips_v2.
  const loadLeagueData = async (
    league: League,
    user?: User | null,
    tipRoundId: number | undefined = state.currentTipRound?.id,
  ) => {
    try {
      setState((prev: AppState) => ({ ...prev, loading: true }));

      // Only fetch data if user is logged in
      if (!user && !state.user) {
        setState((prev: AppState) => ({ ...prev, loading: false }));
        return;
      }

      const isTournament = league.type === "TOURNAMENT";
      const isCup = league.type === "CUP"; // e.g. Champions League Swiss model

      // Tournaments and CUPs don't have a flat standings table — skip the fetch
      // and let their dedicated views (TournamentGroupView / CLTournamentView)
      // load their own data from tournament_structure + tips_tournament.
      const standings = isTournament || isCup
        ? []
        : await leagueService.fetchStandings(league.code);

      // Legacy league-table tip + ranking only apply to type === 'LEAGUE'.
      // Tournament/CUP tips/rankings flow through TournamentService.
      let userTip = null;
      let allTips: TipWithProfile[] = [];
      const currentUser = user || state.user;

      const roundId = tipRoundId;
      if (!isTournament && !isCup && currentUser && roundId) {
        try {
          userTip = await leagueService.getUserTipsForLeague(
            currentUser.id,
            league.id,
            roundId,
          );
        } catch (error) {
          console.error("Error fetching user tips:", error);
          userTip = null;
        }

        try {
          const tipsFromService = await leagueService.getAllTipsForLeague(
            league.id,
            roundId,
          );
          // Transform Tip[] to TipWithProfile[]
          allTips = tipsFromService.map((tip: any) => ({
            ...tip,
            profile: tip.profiles ? {
              user_id: tip.user_id,
              display_name: tip.profiles.display_name
            } : undefined
          }));
        } catch (error) {
          console.error("Error fetching all tips:", error);
          allTips = [];
        }
      }

      setState((prev: AppState) => ({
        ...prev,
        standings,
        userTip,
        allTips,
        leagueStandings: { ...prev.leagueStandings, [league.id]: standings },
        userTips: { ...prev.userTips, [league.id]: userTip },
        allTipsByLeague: { ...prev.allTipsByLeague, [league.id]: allTips },
        loading: false,
      }));
    } catch (error) {
      console.error("Failed to load league data:", error);
      setState((prev: AppState) => ({ ...prev, loading: false }));
    }
  };

  const handleLeagueChange = async (league: League) => {
    setState((prev: AppState) => ({ 
      ...prev, 
      currentLeague: league,
      isBeforeSeasonStart: new Date() < getSeasonStartDate(league)
    }));
    await loadLeagueData(league, state.user);
  };

  const handleTipChange = async (teams: Standing[]) => {
    if (!state.user || !state.currentLeague || !state.currentTipRound) {
      const error = new Error(
        "Cannot save tip: No user / league / tip round selected",
      );
      console.error(error.message);
      throw error;
    }

    try {
      const savedTip = await leagueService.saveUserTipsForLeague(
        state.user.id,
        state.currentLeague.id,
        state.currentTipRound.id,
        teams
      );

      if (savedTip) {
        setState((prev: AppState) => ({
          ...prev,
          userTip: savedTip,
          userTips: { ...prev.userTips, [state.currentLeague!.id]: savedTip },
        }));

        // Reload all tips for updated rankings
        const tipsFromService = await leagueService.getAllTipsForLeague(
          state.currentLeague.id,
          state.currentTipRound.id,
        );
        // Transform Tip[] to TipWithProfile[]
        const allTips: TipWithProfile[] = tipsFromService.map((tip: any) => ({
          ...tip,
          profile: tip.profiles ? {
            user_id: tip.user_id,
            display_name: tip.profiles.display_name
          } : undefined
        }));
        setState((prev: AppState) => ({
          ...prev,
          allTips,
          allTipsByLeague: {
            ...prev.allTipsByLeague,
            [state.currentLeague!.id]: allTips,
          },
        }));
      } else {
        const error = new Error(
          "Failed to save tip - no data returned from server"
        );
        console.error(error.message);
        throw error;
      }
    } catch (error) {
      console.error("Failed to save tip:", error);
      throw error; // Re-throw to let TipTable handle the error state
    }
  };

  const handleLogin = async (user: User) => {
    setState((prev: AppState) => ({ ...prev, user }));

    // Load (or create) the profile via raw REST — supabase v2.0.0 .from()
    // calls hang in this environment, so we use the same pattern as the
    // other services. profileLoaded gates the modal so it doesn't flash
    // during the load window.
    const profile = await profileService.getOrCreate(user.id);
    if (profile) {
      setState((prev: AppState) => ({ ...prev, profile }));
    }
    setProfileLoaded(true);

    // Load tip rounds the user is a member of.
    // After Migration 010 every authenticated user is auto-joined to the
    // "Open Public" round, so this returns at least 1 row.
    try {
      const myRounds = await tipRoundService.listForUser(user.id);
      setState((prev: AppState) => ({
        ...prev,
        myTipRounds: myRounds,
        // Don't auto-select a round — let the user choose on the dashboard.
        // The previous-round preference could be restored from localStorage
        // later as a polish step.
        loading: false,
      }));
    } catch (error) {
      console.error("Failed to load tip rounds after login:", error);
      setState((prev: AppState) => ({ ...prev, loading: false }));
    }
  };

  const handleSelectTipRound = async (round: TipRound) => {
    setState((prev: AppState) => ({
      ...prev,
      currentTipRound: round,
      loading: true,
    }));
    try {
      // Only show competitions that are linked to this round
      const competitions = await tipRoundService.listCompetitions(round.id);
      if (competitions.length > 0) {
        setState((prev: AppState) => ({
          ...prev,
          currentLeague: competitions[0],
          availableLeagues: competitions,
          isBeforeSeasonStart:
            new Date() < getSeasonStartDate(competitions[0]),
        }));
        await loadLeagueData(competitions[0], state.user, round.id);
      } else {
        setState((prev: AppState) => ({
          ...prev,
          currentLeague: null,
          availableLeagues: [],
          loading: false,
        }));
      }
    } catch (error) {
      console.error("Failed to load competitions for tip round:", error);
      setState((prev: AppState) => ({ ...prev, loading: false }));
    }
  };

  const handleSwitchTipRound = () => {
    setState((prev: AppState) => ({
      ...prev,
      currentTipRound: null,
      currentLeague: null,
      availableLeagues: [],
      standings: [],
      userTip: null,
      allTips: [],
    }));
  };

  const handleLogout = async () => {
    setState((prev: AppState) => ({
      ...prev,
      user: null,
      profile: null,
      userTip: null,
      userTips: {},
      currentTipRound: null,
      myTipRounds: [],
    }));
    setProfileLoaded(false);
    setManualEdit(false);
  };

  // Auth state listener and session restoration
  useEffect(() => {
    const initAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          await handleLogin(session.user);
        }
      } catch (error) {
        console.error("Error during auth initialization:", error);
      }
    };

    // Initialize auth state
    initAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (event: string, session: { user: User } | null) => {
        if (event === "SIGNED_IN" && session?.user) {
          await handleLogin(session.user);
        } else if (event === "SIGNED_OUT") {
          await handleLogout();
        } else if (event === "TOKEN_REFRESHED" && session?.user) {
          await handleLogin(session.user);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Profile-name modal: shared across every authed view. Visibility is
  // derived (see needsName / manualEdit above) so it can never get stuck
  // open once the real profile arrives. `required` strips the cancel/skip
  // button when the user still has no real name.
  const profileModal =
    state.user && showProfileEdit ? (
      <ProfileEditModal
        userId={state.user.id}
        profile={state.profile}
        required={needsName}
        onSaved={(p) => {
          setState((prev: AppState) => ({ ...prev, profile: p }));
          setManualEdit(false);
        }}
        onClose={() => setManualEdit(false)}
      />
    ) : null;

  // Legal pages take precedence over everything — accessible logged-in or out.
  if (legalPage) {
    return (
      <Suspense fallback={<ChunkSpinner />}>
        <LegalPage kind={legalPage} onBack={() => setLegalPage(null)} />
      </Suspense>
    );
  }

  // Show landing page (with embedded Auth) when:
  //   - no user is logged in (entry point) — onBackToApp is null
  //   - user clicked the logo to revisit the marketing content
  if (!state.user || showLanding) {
    return (
      <Suspense fallback={<ChunkSpinner />}>
        <LandingPage
          user={state.user}
          profile={state.profile}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onOpenLegal={setLegalPage}
          pendingJoinCode={pendingJoinCode}
          onBackToApp={
            state.user ? () => setShowLanding(false) : undefined
          }
        />
        <CookieHint onMoreInfo={() => setLegalPage("privacy")} />
      </Suspense>
    );
  }

  // TipRound detail view (member list, invite share, competition manage).
  // Takes precedence over both the dashboard and the main app — so the user
  // can manage a round even while inside another.
  if (managingRound) {
    return (
      <Suspense fallback={<ChunkSpinner />}>
        <AppHeader
          user={state.user}
          profile={state.profile}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onEditName={() => setManualEdit(true)}
          onHomeClick={() => setShowLanding(true)}
        />
        <TipRoundDetail
          round={managingRound}
          user={state.user}
          onBack={() => setManagingRound(null)}
          onOpenRound={(round) => {
            setManagingRound(null);
            handleSelectTipRound(round);
          }}
        />
        <SiteFooter onOpenLegal={setLegalPage} />
        <CookieHint onMoreInfo={() => setLegalPage("privacy")} />
        {profileModal}
      </Suspense>
    );
  }

  // Tippgruppen dashboard: shown once logged in but no round picked yet.
  // The user picks (or creates / joins) a round, which then loads its
  // competitions and lands them on the main app.
  if (!state.currentTipRound) {
    return (
      <Suspense fallback={<ChunkSpinner />}>
        <AppHeader
          user={state.user}
          profile={state.profile}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onEditName={() => setManualEdit(true)}
          onHomeClick={() => setShowLanding(true)}
        />
        <TipRoundDashboard
          user={state.user}
          onSelectRound={handleSelectTipRound}
          onManageRound={setManagingRound}
          initialJoinCode={pendingJoinCode ?? undefined}
          onJoinCodeConsumed={() => setPendingJoinCode(null)}
        />
        <SiteFooter onOpenLegal={setLegalPage} />
        <CookieHint onMoreInfo={() => setLegalPage("privacy")} />
        {profileModal}
        {isAdmin && (
          <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 900, display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => setShowAdminOddsPanel(true)}
              style={{
                padding: "10px 18px",
                background: "linear-gradient(135deg, #0891b2 0%, #6366f1 100%)",
                border: "none", borderRadius: "12px", color: "white",
                fontSize: "13px", fontWeight: 700, cursor: "pointer",
                boxShadow: "0 8px 24px rgba(8,145,178,0.45)",
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              📊 Quoten
            </button>
            <button
              type="button"
              onClick={() => setShowAdminPanel(true)}
              style={{
                padding: "10px 18px",
                background: "linear-gradient(135deg, #7c3aed 0%, #db2777 100%)",
                border: "none", borderRadius: "12px", color: "white",
                fontSize: "13px", fontWeight: 700, cursor: "pointer",
                boxShadow: "0 8px 24px rgba(124,58,237,0.45)",
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              🛡️ Admin
            </button>
          </div>
        )}
        {isAdmin && showAdminPanel && state.user && (
          <AdminRoundsPanel
            currentUserId={state.user.id}
            onOpenRound={(round) => { setShowAdminPanel(false); handleSelectTipRound(round); }}
            onClose={() => setShowAdminPanel(false)}
          />
        )}
        {isAdmin && showAdminOddsPanel && (
          <AdminOddsPanel onClose={() => setShowAdminOddsPanel(false)} />
        )}
      </Suspense>
    );
  }

  // Show loading if a tip round is selected but its competitions are loading
  if (!state.currentLeague) {
    return (
      <div className="loading-container">
        <div className="loading">{t("app.loadingCompetitions")}</div>
      </div>
    );
  }

  return (
    <Suspense fallback={<ChunkSpinner />}>
      <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
      <div className="app">
        {/* Header */}
        <AppHeader
          user={state.user}
          profile={state.profile}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onEditName={() => setManualEdit(true)}
          onHomeClick={() => setShowLanding(true)}
          middleSlot={
            <>
              {/* Active tip round + switcher */}
              <button
                type="button"
                onClick={handleSwitchTipRound}
                title={t("nav.switchRound")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 16px",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
                  border: "1px solid rgba(34, 211, 238, 0.35)",
                  borderRadius: "20px",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily:
                    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  letterSpacing: "-0.01em",
                  maxWidth: "260px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  boxShadow:
                    "0 0 0 1px rgba(34,211,238,0.05) inset, 0 8px 24px -12px rgba(34,211,238,0.4)",
                }}
              >
                <span style={{ fontSize: "16px" }}>👥</span>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {state.currentTipRound.name}
                </span>
                <span style={{ opacity: 0.6, fontSize: "12px" }}>↔</span>
              </button>
              <LeagueSelector
                currentLeague={state.currentLeague}
                onLeagueChange={handleLeagueChange}
                availableLeagues={state.availableLeagues}
              />
            </>
          }
        />

        {/* Main Content */}
        <main className="app-main">
          {state.loading ? (
            <div className="loading">{t("common.loading")}</div>
          ) : (
            <div className="content-layout">
              {/* Rankings Table - Hauptfokus, mittig und allein oben */}
              <div className="rankings-section-primary">
                {state.currentLeague?.type === "TOURNAMENT" || state.currentLeague?.type === "CUP" ? (
                  <TournamentRankingsTable
                    competitionId={state.currentLeague.id}
                    tipRoundId={state.currentTipRound!.id}
                    isMobile={state.isMobile}
                    isBeforeLock={state.isBeforeSeasonStart}
                    competitionType={state.currentLeague.type}
                    actualLeaguePhaseOrder={cupLeaguePhaseOrder}
                    onSelectPlayer={setHighlightedUserId}
                    selectedUserId={highlightedUserId}
                  />
                ) : (
                  <RankingsTable
                    allTips={state.allTips}
                    standings={state.standings}
                    isMobile={state.isMobile}
                    tipRoundId={state.currentTipRound?.id}
                    leagueId={state.currentLeague?.id}
                    gameMode={state.currentTipRound?.game_mode}
                    classicScoringRule={state.currentTipRound?.classic_scoring_rule}
                    onSelectPlayer={setHighlightedUserId}
                    selectedUserId={highlightedUserId}
                  />
                )}
              </div>

              {/* Secondary content: differs per competition type */}
              {state.currentLeague?.type === "CUP" ? (
                state.user && state.currentTipRound ? (
                  <>
                    <CupComparisonPanel
                      data={cupTipData.data}
                      loading={cupTipData.loading}
                      currentUserId={state.user.id}
                      highlightUserId={highlightedUserId}
                      isMobile={state.isMobile}
                    />
                    <div className="tip-section">
                      <CLTournamentView
                        competitionId={state.currentLeague.id}
                        competitionCode={state.currentLeague.code}
                        tipRoundId={state.currentTipRound.id}
                        userId={state.user.id}
                        isMobile={state.isMobile}
                      />
                    </div>
                    <SpecialQuestionsPanel
                      tipRoundId={state.currentTipRound.id}
                      leagueId={state.currentLeague.id}
                      userId={state.user.id}
                      isMobile={state.isMobile}
                    />
                  </>
                ) : null
              ) : state.currentLeague?.type === "TOURNAMENT" ? (
                state.user ? (
                  <div className="tip-section">
                    <TournamentGroupView
                      competitionId={state.currentLeague.id}
                      tipRoundId={state.currentTipRound!.id}
                      userId={state.user.id}
                      isBeforeLock={state.isBeforeSeasonStart}
                    />
                  </div>
                ) : null
              ) : (
                <>
                  <div
                    className={`secondary-content-grid ${
                      !state.user ? "single-item" : ""
                    }`}
                  >
                    {/* Tipps der Mitspieler — steht, wo bisher die aktuelle
                        Tabelle stand, und trägt sie als vierten Reiter weiter. */}
                    <div className="standings-section">
                      <TipComparisonPanel
                        data={leagueTipData.data}
                        loading={leagueTipData.loading}
                        allTips={state.allTips}
                        standings={state.standings}
                        currentUserId={state.user?.id}
                        currentLeagueName={state.currentLeague?.name || ""}
                        gameMode={state.currentTipRound.game_mode}
                        classicScoringRule={state.currentTipRound.classic_scoring_rule}
                        isBeforeLock={state.isBeforeSeasonStart}
                        lockDate={state.currentLeague?.lock_date}
                        highlightUserId={highlightedUserId}
                        isMobile={state.isMobile}
                      />
                    </div>

                    {/* Tip Interface - Only show when user is logged in.
                        Klassik mode: the single-match tip is primary here;
                        the table tip becomes an optional add-on below. */}
                    {state.user && (
                      <div className="tip-section">
                        {state.currentTipRound.game_mode === "klassisch" ? (
                          <MatchTipTable
                            tipRoundId={state.currentTipRound.id}
                            leagueId={state.currentLeague.id}
                            userId={state.user.id}
                            classicScoringRule={state.currentTipRound.classic_scoring_rule}
                            isMobile={state.isMobile}
                          />
                        ) : (
                          <TipTable
                            standings={state.standings}
                            userTip={state.userTip}
                            onTipChange={handleTipChange}
                            isBeforeSeasonStart={state.isBeforeSeasonStart}
                            isMobile={state.isMobile}
                            allTips={state.allTips}
                            lockDate={state.currentLeague?.lock_date}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Klassik-Modus: optionales Tabellen-Tipp-Add-on. Same
                      lock (isBeforeSeasonStart) and component as the
                      "tabellen" mode's primary tip — collapsed by default so
                      it doesn't compete with the primary match tip above. */}
                  {state.user && state.currentTipRound.game_mode === "klassisch" && (
                    <details className="table-addon">
                      <summary className="table-addon-summary">
                        {t("classicMode.tableAddon.heading")}
                      </summary>
                      <p className="table-addon-desc">
                        {t("classicMode.tableAddon.desc")}
                      </p>
                      <TipTable
                        standings={state.standings}
                        userTip={state.userTip}
                        onTipChange={handleTipChange}
                        isBeforeSeasonStart={state.isBeforeSeasonStart}
                        isMobile={state.isMobile}
                        allTips={state.allTips}
                        lockDate={state.currentLeague?.lock_date}
                        title={t("classicMode.tableAddon.tipTitle")}
                      />
                    </details>
                  )}

                  {/* Special Questions Panel — shown for all logged-in users in league mode */}
                  {state.user && state.currentTipRound && state.currentLeague && (
                    <SpecialQuestionsPanel
                      tipRoundId={state.currentTipRound.id}
                      leagueId={state.currentLeague.id}
                      userId={state.user.id}
                      isMobile={state.isMobile}
                    />
                  )}

                  {/* Live-Drama Panel — only shown once at least one matchday has a snapshot */}
                  {state.user && state.currentTipRound && state.currentLeague && (
                    <LeagueDramaPanel
                      tipRoundId={state.currentTipRound.id}
                      leagueId={state.currentLeague.id}
                      userId={state.user.id}
                      isMobile={state.isMobile}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </main>

        <SiteFooter variant="app" onOpenLegal={setLegalPage} />

        {/* Invite Modal */}
        {state.showInviteModal && (
          <InviteFriendsModal
            onClose={() =>
              setState((prev: AppState) => ({
                ...prev,
                showInviteModal: false,
              }))
            }
          />
        )}

        <CookieHint onMoreInfo={() => setLegalPage("privacy")} />
        {profileModal}
        {isAdmin && (
          <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 900, display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => setShowAdminOddsPanel(true)}
              style={{
                padding: "10px 18px",
                background: "linear-gradient(135deg, #0891b2 0%, #6366f1 100%)",
                border: "none", borderRadius: "12px", color: "white",
                fontSize: "13px", fontWeight: 700, cursor: "pointer",
                boxShadow: "0 8px 24px rgba(8,145,178,0.45)",
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              📊 Quoten
            </button>
            <button
              type="button"
              onClick={() => setShowAdminPanel(true)}
              style={{
                padding: "10px 18px",
                background: "linear-gradient(135deg, #7c3aed 0%, #db2777 100%)",
                border: "none", borderRadius: "12px", color: "white",
                fontSize: "13px", fontWeight: 700, cursor: "pointer",
                boxShadow: "0 8px 24px rgba(124,58,237,0.45)",
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              🛡️ Admin
            </button>
          </div>
        )}
        {isAdmin && showAdminPanel && state.user && (
          <AdminRoundsPanel
            currentUserId={state.user.id}
            onOpenRound={(round) => { setShowAdminPanel(false); handleSelectTipRound(round); }}
            onClose={() => setShowAdminPanel(false)}
          />
        )}
        {isAdmin && showAdminOddsPanel && (
          <AdminOddsPanel onClose={() => setShowAdminOddsPanel(false)} />
        )}
      </div>
      </DndProvider>
    </Suspense>
  );
}

export default App;
